export type RichTextSegmentType = { font?: { vertAlign?: "superscript" | "subscript"; bold?: boolean }; text: string };
export type CellRichValueType = { richText: RichTextSegmentType[] };

/**
 * Decodes double-encoded HTML entities (e.g. `&gt;` → `>`, `&deg;` → `°`)
 * that survive the DOM's initial parsing. Uses a temporary element to leverage
 * the browser's built-in HTML parser for full entity coverage.
 */
const entityDecoder = document.createElement("textarea");
export const decodeHtmlEntities = (text: string): string => {
  if (!text.includes("&")) {
    return text;
  }
  entityDecoder.innerHTML = text;
  return entityDecoder.value;
};

/**
 * Converts a table cell's HTML content into an ExcelJS cell value.
 *
 * - <br>  → newline (\n) between lines
 * - <sup> → superscript rich-text segment
 * - <sub> → subscript rich-text segment
 * - <strong> / <b> → bold rich-text segment
 *
 * Returns a plain string when no inline formatting is needed; otherwise returns
 * a RichText object so Excel renders e.g. "Spectrum b" with the "b" superscripted.
 */
export const cellValue = (cell: HTMLElement): string | CellRichValueType => {
  const segments: RichTextSegmentType[] = [];

  type FontStateType = { vertAlign?: "superscript" | "subscript"; bold?: boolean };

  const walk = (node: Node, fontState: FontStateType): void => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = decodeHtmlEntities(node.textContent ?? "");
      if (text) {
        const hasFont = fontState.vertAlign !== undefined || fontState.bold;
        segments.push(hasFont ? { font: { ...fontState }, text } : { text });
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      // Skip the injected cart button subtree — its text ("Request Price",
      // "Not Available", "Loading…") must not appear in exported spreadsheets.
      if (el.classList?.contains("tl-spec-table-cart__mount")) {
        return;
      }
      const tag = el.tagName.toLowerCase();
      if (tag === "br") {
        segments.push({ text: "\n" });
      } else if (tag === "sup") {
        el.childNodes.forEach((child) => walk(child, { ...fontState, vertAlign: "superscript" }));
      } else if (tag === "sub") {
        el.childNodes.forEach((child) => walk(child, { ...fontState, vertAlign: "subscript" }));
      } else if (tag === "strong" || tag === "b") {
        el.childNodes.forEach((child) => walk(child, { ...fontState, bold: true }));
      } else {
        el.childNodes.forEach((child) => walk(child, fontState));
      }
    }
  };

  cell.childNodes.forEach((child) => walk(child, {}));

  // Merge adjacent segments with identical font properties to reduce array size
  const merged: RichTextSegmentType[] = [];
  for (const seg of segments) {
    const last = merged[merged.length - 1];
    if (last && last.font?.vertAlign === seg.font?.vertAlign && Boolean(last.font?.bold) === Boolean(seg.font?.bold)) {
      last.text += seg.text;
    } else {
      merged.push({ ...seg });
    }
  }

  // Collapse internal whitespace (but preserve newlines) and drop empty segments
  const cleaned: RichTextSegmentType[] = merged
    .map((s) => ({ ...s, text: s.text.replace(/[^\S\n]+/g, " ") }))
    .filter((s) => s.text);

  // Trim leading/trailing space from the whole value
  if (cleaned.length > 0) {
    cleaned[0].text = cleaned[0].text.trimStart();
    cleaned[cleaned.length - 1].text = cleaned[cleaned.length - 1].text.trimEnd();
  }

  const hasFormatting = cleaned.some((s) => s.font?.vertAlign !== undefined || s.font?.bold);
  if (!hasFormatting) {
    return cleaned.map((s) => s.text).join("");
  }
  return { richText: cleaned };
};

/**
 * Applies bold font to every segment of a cell value.
 * Converts a plain string to a single bold rich-text segment when needed.
 */
export const applyBold = (value: string | CellRichValueType): CellRichValueType => {
  if (typeof value === "string") {
    return { richText: [{ font: { bold: true }, text: value }] };
  }
  return {
    richText: value.richText.map((seg) => ({ ...seg, font: { ...seg.font, bold: true } })),
  };
};
